import { useState } from "react";
import { FeatureForm } from "../components/FeatureForm";
import { UserFeaturePanel } from "../components/UserFeaturePanel";
import {
	type Feature,
	type FeatureFlagUser,
	useDeleteFeature,
	useFeatureFlagUsers,
	useFeatures,
} from "../hooks/useFeatures";

type TabType = "features" | "users";

interface FeatureCardProps {
	feature: Feature;
	onEdit: (feature: Feature) => void;
	onDelete: (featureKey: string) => void;
	isDeleting: boolean;
}

function FeatureCard({
	feature,
	onEdit,
	onDelete,
	isDeleting,
}: FeatureCardProps) {
	const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

	const handleDelete = () => {
		onDelete(feature.featureKey);
		setShowDeleteConfirm(false);
	};

	return (
		<div className="card mb-4">
			<div className="flex items-start justify-between">
				<div className="flex-1">
					<div className="flex items-center gap-3">
						<h3 className="font-mono text-lg font-semibold text-white">
							{feature.featureKey}
						</h3>
						{feature.enabledForAll && (
							<span className="rounded-full bg-green-500/20 px-2 py-1 text-xs font-medium text-green-400">
								Enabled for All
							</span>
						)}
					</div>
					<p className="mt-1 text-base text-gray-300">{feature.name}</p>
					{feature.description && (
						<p className="mt-1 text-sm text-gray-500">{feature.description}</p>
					)}
					<p className="mt-2 text-xs text-gray-500">
						{feature.enabledUserCount}{" "}
						{feature.enabledUserCount === 1 ? "user" : "users"} enabled
					</p>
				</div>

				<div className="ml-4 flex gap-2">
					{showDeleteConfirm ? (
						<div className="flex flex-col gap-2">
							<p className="text-xs text-gray-400">Delete this feature?</p>
							<div className="flex gap-2">
								<button
									type="button"
									onClick={handleDelete}
									disabled={isDeleting}
									className="btn-danger text-sm"
								>
									{isDeleting ? "Deleting..." : "Yes, Delete"}
								</button>
								<button
									type="button"
									onClick={() => setShowDeleteConfirm(false)}
									className="btn-secondary text-sm"
								>
									Cancel
								</button>
							</div>
						</div>
					) : (
						<>
							<button
								type="button"
								onClick={() => onEdit(feature)}
								className="btn-secondary text-sm"
							>
								Edit
							</button>
							<button
								type="button"
								onClick={() => setShowDeleteConfirm(true)}
								className="text-sm text-gray-400 hover:text-red-400"
								title="Delete feature"
							>
								<svg
									className="h-5 w-5"
									fill="none"
									viewBox="0 0 24 24"
									stroke="currentColor"
									strokeWidth={1.5}
									aria-hidden="true"
								>
									<path
										strokeLinecap="round"
										strokeLinejoin="round"
										d="m14.74 9-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 0 1-2.244 2.077H8.084a2.25 2.25 0 0 1-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 0 0-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 0 1 3.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 0 0-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 0 0-7.5 0"
									/>
								</svg>
							</button>
						</>
					)}
				</div>
			</div>
		</div>
	);
}

interface UserRowProps {
	user: FeatureFlagUser;
	onManage: (user: FeatureFlagUser) => void;
}

function UserRow({ user, onManage }: UserRowProps) {
	const formatDate = (dateString: string | null | undefined) => {
		if (!dateString) return "Never";
		return new Date(dateString).toLocaleDateString("en-US", {
			year: "numeric",
			month: "short",
			day: "numeric",
			hour: "2-digit",
			minute: "2-digit",
		});
	};

	return (
		<div className="card mb-4">
			<div className="flex items-start justify-between">
				<div className="flex-1">
					<h3 className="text-lg font-semibold text-white">{user.email}</h3>
					<p className="mt-1 text-sm text-gray-500">
						Last login: {formatDate(user.lastLoginAt)}
					</p>
					{user.enabledFeatures && user.enabledFeatures.length > 0 && (
						<div className="mt-2 flex flex-wrap gap-1">
							{user.enabledFeatures.map((featureKey) => (
								<span
									key={featureKey}
									className="rounded bg-indigo-500/20 px-2 py-0.5 font-mono text-xs text-indigo-400"
								>
									{featureKey}
								</span>
							))}
						</div>
					)}
					{(!user.enabledFeatures || user.enabledFeatures.length === 0) && (
						<p className="mt-2 text-xs text-gray-500">No features enabled</p>
					)}
				</div>
				<div className="ml-4">
					<button
						type="button"
						onClick={() => onManage(user)}
						className="btn-secondary text-sm"
					>
						Manage
					</button>
				</div>
			</div>
		</div>
	);
}

export function FeaturesPage() {
	const [activeTab, setActiveTab] = useState<TabType>("features");
	const [searchQuery, setSearchQuery] = useState("");
	const [editingFeature, setEditingFeature] = useState<Feature | null>(null);
	const [managingUser, setManagingUser] = useState<FeatureFlagUser | null>(
		null,
	);
	const [showCreateForm, setShowCreateForm] = useState(false);

	// Query hooks
	const {
		data: featuresData,
		isLoading: featuresLoading,
		error: featuresError,
		refetch: refetchFeatures,
	} = useFeatures();
	const {
		data: usersData,
		isLoading: usersLoading,
		error: usersError,
		refetch: refetchUsers,
	} = useFeatureFlagUsers(searchQuery || undefined);

	// Mutation hooks
	const deleteFeature = useDeleteFeature();

	const handleDeleteFeature = async (featureKey: string) => {
		try {
			await deleteFeature.mutateAsync(featureKey);
		} catch (err) {
			console.error("Failed to delete feature:", err);
		}
	};

	const handleEditFeature = (feature: Feature) => {
		setEditingFeature(feature);
	};

	const handleManageUser = (user: FeatureFlagUser) => {
		setManagingUser(user);
	};

	const tabs: { value: TabType; label: string }[] = [
		{ value: "features", label: "Features" },
		{ value: "users", label: "Users" },
	];

	const features = featuresData?.edges.map((edge) => edge.node) ?? [];
	const users = usersData?.edges.map((edge) => edge.node) ?? [];

	return (
		<div className="space-y-6">
			{/* Header */}
			<div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
				<div>
					<h2 className="text-2xl font-bold text-white">Feature Flags</h2>
					<p className="text-gray-400">Manage features and user access</p>
				</div>
				{activeTab === "features" && (
					<div className="flex gap-2">
						<button
							type="button"
							onClick={() => refetchFeatures()}
							className="btn-secondary w-full sm:w-auto"
						>
							Refresh
						</button>
						<button
							type="button"
							onClick={() => setShowCreateForm(true)}
							className="btn-primary w-full sm:w-auto"
						>
							+ New Feature
						</button>
					</div>
				)}
				{activeTab === "users" && (
					<button
						type="button"
						onClick={() => refetchUsers()}
						className="btn-secondary w-full sm:w-auto"
					>
						Refresh
					</button>
				)}
			</div>

			{/* Tabs */}
			<div className="flex gap-2 border-b border-gray-800 pb-2">
				{tabs.map((tab) => (
					<button
						type="button"
						key={tab.value}
						onClick={() => setActiveTab(tab.value)}
						className={`rounded-t-md px-4 py-2 text-sm font-medium transition-colors ${
							activeTab === tab.value
								? "bg-indigo-600 text-white"
								: "bg-gray-700 text-gray-300 hover:bg-gray-600"
						}`}
					>
						{tab.label}
					</button>
				))}
			</div>

			{/* Tab Content */}
			{activeTab === "features" && (
				<div>
					{featuresLoading ? (
						<div className="py-12 text-center text-gray-400">
							Loading features...
						</div>
					) : featuresError ? (
						<div className="py-12 text-center text-red-400">
							Failed to load features. Please try again.
						</div>
					) : features.length === 0 ? (
						<div className="py-12 text-center text-gray-400">
							No features defined yet. Create one to get started.
						</div>
					) : (
						<div>
							{features.map((feature) => (
								<FeatureCard
									key={feature.id}
									feature={feature}
									onEdit={handleEditFeature}
									onDelete={handleDeleteFeature}
									isDeleting={deleteFeature.isPending}
								/>
							))}
						</div>
					)}
				</div>
			)}

			{activeTab === "users" && (
				<div>
					{/* Search Input */}
					<div className="mb-6">
						<input
							type="text"
							value={searchQuery}
							onChange={(e) => setSearchQuery(e.target.value)}
							placeholder="Search by email..."
							className="input w-full max-w-md"
						/>
					</div>

					{usersLoading ? (
						<div className="py-12 text-center text-gray-400">
							Loading users...
						</div>
					) : usersError ? (
						<div className="py-12 text-center text-red-400">
							Failed to load users. Please try again.
						</div>
					) : users.length === 0 ? (
						<div className="py-12 text-center text-gray-400">
							{searchQuery
								? `No users found matching "${searchQuery}".`
								: "No users found. Users will appear here after they log in."}
						</div>
					) : (
						<div>
							{users.map((user) => (
								<UserRow
									key={user.id}
									user={user}
									onManage={handleManageUser}
								/>
							))}
						</div>
					)}
				</div>
			)}

			{/* Feature Create/Edit Modal */}
			{showCreateForm && (
				<FeatureForm
					onSuccess={() => setShowCreateForm(false)}
					onCancel={() => setShowCreateForm(false)}
				/>
			)}

			{editingFeature && (
				<FeatureForm
					feature={editingFeature}
					onSuccess={() => setEditingFeature(null)}
					onCancel={() => setEditingFeature(null)}
				/>
			)}

			{managingUser && (
				<UserFeaturePanel
					user={managingUser}
					onClose={() => setManagingUser(null)}
				/>
			)}
		</div>
	);
}
